import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "page-shell" }

import { computed, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import AppCard from "@/shared/components/ui/AppCard.vue";
import AppTab from "@/shared/components/ui/AppTab.vue";
import AppTextField from "@/shared/components/ui/AppTextField.vue";
import LoadingState from "@/shared/components/feedback/LoadingState.vue";
import AppButtonLoadMore from "@/shared/components/ui/AppButtonLoadMore.vue";
import { fetchMessages } from "@/shared/services/messages.api";
import { messagePresets } from "./messagePresets";

type MessageItem = {
  id: string;
  title: string;
  preview: string;
  time: string;
  initials: string;
  avatarBg: string;
  unreadCount: number;
  isOnline: boolean;
};

type MessagePagination = {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
};

type MessageApiRecord = Record<string, unknown>;


export default /*@__PURE__*/_defineComponent({
  __name: 'CompanyApplyPage',
  setup(__props) {

const activeTab = ref<"all" | "unread">("all");
const searchQuery = ref("");
const router = useRouter();
const unreadCount = ref(0);
const tabs = computed<Array<{ value: "all" | "unread"; label: string }>>(() => [
  { value: "all", label: "All" },
  { value: "unread", label: `Unread (${unreadCount.value || unreadTotal.value})` },
]);

const messages = ref<MessageItem[]>(
  messagePresets.map((item) => ({
    id: item.chat_thread_id,
    title: item.title,
    preview: item.preview,
    time: item.time,
    initials: item.initials,
    avatarBg: item.avatar_logo,
    unreadCount: item.unread_count,
    isOnline: item.is_online,
  }))
);
const isLoading = ref(false);
const errorMessage = ref("");
const isLoadingMore = ref(false);
const isSearching = ref(false);

const pagination = ref<MessagePagination>({ current_page: 1, last_page: 1, per_page: 10, total: 0 });
const hasMore = ref(false);
let loadRequestId = 0;
let searchTimer: ReturnType<typeof setTimeout> | null = null;

const avatarPalette = ["#dbeafe", "#dcfce7", "#ede9fe", "#fef3c7", "#fde2e4", "#ffedd5"];

function asString(value: unknown, fallback = "") {
  if (typeof value === "string" || typeof value === "number") return String(value);
  return fallback;
}

function asNumber(value: unknown, fallback = 0) {
  const parsed = Number(value);
  return Number.isNaN(parsed) ? fallback : parsed;
}

function getInitials(value: string) {
  const parts = value.trim().split(/\s+/).slice(0, 2);
  const initials = parts.map((part) => part.charAt(0)).join("");
  return initials || "NA";
}

function normalizeMessage(item: MessageApiRecord, index: number): MessageItem {
  const title = asString(item.title || item.name || item.vendor_name || item.sender_name, "Message");
  return {
    id: asString(item.id || item.conversation_id || `msg-${index}`),
    title,
    preview: asString(item.preview || item.last_message || item.message || item.snippet, ""),
    time: asString(item.time || item.updated_at || item.created_at, "-"),
    initials: asString(item.initials || getInitials(title)),
    avatarBg: asString(item.avatar_bg || item.avatarBg || avatarPalette[index % avatarPalette.length]),
    unreadCount: asNumber(item.unread_count ?? item.unreadCount ?? 0, 0),
    isOnline: Boolean(item.is_online ?? item.isOnline ?? false),
  };
}

function extractMessageList(payload: unknown): MessageApiRecord[] {
  if (Array.isArray(payload)) return payload as MessageApiRecord[];

  const wrapped = payload as { data?: unknown; conversations?: unknown; items?: unknown };
  if (Array.isArray(wrapped?.data)) return wrapped.data as MessageApiRecord[];
  if (Array.isArray(wrapped?.conversations)) return wrapped.conversations as MessageApiRecord[];
  if (Array.isArray(wrapped?.items)) return wrapped.items as MessageApiRecord[];
  return [];
}

function extractMeta(payload: unknown): MessagePagination {
  const meta = (payload as any)?.meta || {};
  return {
    current_page: Number(meta.current_page) || 1,
    last_page: Number(meta.last_page) || 1,
    per_page: Number(meta.per_page) || 10,
    total: Number(meta.total) || 0,
  };
}

const unreadTotal = computed(() =>
  messages.value.reduce((sum, item) => sum + item.unreadCount, 0),
);

const filteredMessages = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  let list = messages.value;
  if (activeTab.value === "unread") {
    list = list.filter((item) => item.unreadCount > 0);
  }
  // Server already filters by search term; avoid double-filtering here.
  return list;
});

async function loadMessages(page = 1, append = false, reason: "default" | "search" | "append" = "default") {
  const requestId = ++loadRequestId;
  if (reason === "append") {
    isLoadingMore.value = true;
  } else if (reason === "search") {
    isSearching.value = true;
  } else {
    isLoading.value = true;
    errorMessage.value = "";
  }

  try {
    const response = await fetchMessages({ page, search: searchQuery.value.trim() || undefined });
    if (requestId !== loadRequestId) return; // stale

    const list = extractMessageList(response).map((item, index) => normalizeMessage(item, index));
    const meta = extractMeta(response);
    pagination.value = meta;
    hasMore.value = meta.current_page < meta.last_page;
    const apiUnread = (response as any)?.message_count?.unread;
    unreadCount.value = Number.isFinite(apiUnread) ? Number(apiUnread) : unreadTotal.value;

    if (reason === "append") {
      const existingIds = new Set(messages.value.map((m) => m.id));
      const merged = [...messages.value, ...list.filter((m) => !existingIds.has(m.id))];
      messages.value = merged;
    } else {
      messages.value = list.length ? list : [];
    }
  } catch (error) {
    console.error(error);
    if (reason !== "append") {
      errorMessage.value = "Unable to load messages. Please try again.";
    }
  } finally {
    if (requestId === loadRequestId) {
      if (reason === "append") {
        isLoadingMore.value = false;
      } else if (reason === "search") {
        isSearching.value = false;
      } else {
        isLoading.value = false;
      }
    }
  }
}

function loadMore() {
  if (isLoadingMore.value || !hasMore.value) return;
  const nextPage = pagination.value.current_page + 1;
  loadMessages(nextPage, true, "append");
}

function handleSearchClear() {
  if (searchTimer) {
    clearTimeout(searchTimer);
    searchTimer = null;
  }
  searchQuery.value = "";
  loadMessages(1, false, "search");
}

watch(
  () => searchQuery.value,
  (value) => {
    if (searchTimer) clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      loadMessages(1, false, "search");
    }, 300);
  },
);

onMounted(() => {
  loadMessages();
});

function handleOpen(id: string) {
  router.push({ name: "customer.messages.detail", params: { id } });
}

return (_ctx: any,_cache: any) => {
  const _component_v_btn = _resolveComponent("v-btn")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(AppCard, null, {
      default: _withCtx(() => [
        _cache[2] || (_cache[2] = _createElementVNode("p", null, "name and logo ", -1)),
        _cache[3] || (_cache[3] = _createElementVNode("p", null, "address", -1)),
        _createVNode(_component_v_btn, {
          color: "primary",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(router).push({name:'join-as-vendor.form'})))
        }, {
          default: _withCtx(() => [...(_cache[1] || (_cache[1] = [
            _createTextVNode("apply now", -1)
          ]))]),
          _: 1
        })
      ]),
      _: 1
    })
  ]))
}
}

})